import {
  ADMIN_APPEAL_OUTCOMES,
  ADMIN_APPEAL_PENALTY_TYPES,
  type ResolveAppealBody,
} from '@carcheck/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useEffect, useMemo, type ReactElement } from 'react';
import { Controller, useForm, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OutcomeSelect } from './ResolveAppealFields/OutcomeSelect';
import { PenaltyFields } from './ResolveAppealFields/PenaltyFields';
import { RefundFields } from './ResolveAppealFields/RefundFields';
import {
  buildResolveBody,
  makeResolveSchema,
  RESOLVE_FORM_DEFAULTS,
  type ResolveFormValues,
} from '../_lib/resolveAppealSchema';

/**
 * Resolve-appeal dialog (AUT-130, architect 🟠 #1 / #2). Outcome-first form
 * bound to `makeResolveSchema(orderTotal)` (RHF+Zod) so client validation
 * matches the backend `ResolveAppealSchema` 400s. Built to the CONTRACT,
 * not the mockup's fused calculator (the 4 outcomes are mutually
 * exclusive).
 *
 * Shape:
 *   - Outcome `<Select>` over the 4 `ADMIN_APPEAL_OUTCOMES`.
 *   - Conditional `refund_partial` → amount OR percentage (RefundFields;
 *     refundAmount clamped `<= orderTotal`).
 *   - Conditional `inspector_penalty` → penalty-type select
 *     (`ADMIN_APPEAL_PENALTY_TYPES`) + days field shown only for
 *     `suspension` (PenaltyFields).
 *   - Required `decisionReason` textarea (min 5 — the mockup omits it;
 *     without it every resolve 400s).
 *   - Optional `adminNotes`.
 *
 * The mutation (`useResolveAppeal`, proactive step-up) is fired by the
 * parent on confirm with the projected `ResolveAppealBody`. The dialog
 * never touches step-up itself — it just collects + validates + projects.
 */
export interface ResolveAppealDialogProps {
  open: boolean;
  loading?: boolean;
  /** Order total (parsed number) for the `refundAmount <= total` clamp + hint. */
  orderTotal: number;
  onConfirm: (body: ResolveAppealBody) => void;
  onCancel: () => void;
}

/** The required decision-reason textarea (customer-visible justification). */
const DecisionReasonField = ({
  control,
  loading,
}: {
  control: Control<ResolveFormValues>;
  loading: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Controller
      name="decisionReason"
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          fullWidth
          multiline
          minRows={3}
          size="small"
          required
          disabled={loading}
          label={t('appeals.resolveDialog.decisionReasonLabel')}
          placeholder={t('appeals.resolveDialog.decisionReasonPlaceholder')}
          error={Boolean(fieldState.error)}
          helperText={
            fieldState.error
              ? t(fieldState.error.message ?? '')
              : t('appeals.resolveDialog.decisionReasonHint')
          }
        />
      )}
    />
  );
};

/** Optional internal admin-notes textarea (never shown to the customer). */
const AdminNotesField = ({
  control,
  loading,
}: {
  control: Control<ResolveFormValues>;
  loading: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Controller
      name="adminNotes"
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          fullWidth
          multiline
          minRows={2}
          size="small"
          disabled={loading}
          label={t('appeals.resolveDialog.adminNotesLabel')}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error ? t(fieldState.error.message ?? '') : ' '}
        />
      )}
    />
  );
};

export const ResolveAppealDialog = ({
  open,
  loading = false,
  orderTotal,
  onConfirm,
  onCancel,
}: ResolveAppealDialogProps): ReactElement => {
  const { t } = useTranslation();
  const schema = useMemo(() => makeResolveSchema(orderTotal), [orderTotal]);
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<ResolveFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: RESOLVE_FORM_DEFAULTS,
  });

  useEffect(() => {
    if (open) reset(RESOLVE_FORM_DEFAULTS);
  }, [open, reset]);

  const outcome = watch('outcome');
  const penaltyType = watch('inspectorPenaltyType');

  const submit = (values: ResolveFormValues): void => {
    if (loading) return;
    onConfirm(buildResolveBody(values));
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{t('appeals.resolveDialog.title')}</DialogTitle>
      <form onSubmit={handleSubmit(submit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DialogContentText>{t('appeals.resolveDialog.description')}</DialogContentText>

          <OutcomeSelect control={control} loading={loading} outcomes={ADMIN_APPEAL_OUTCOMES} />

          {outcome === 'refund_partial' ? (
            <RefundFields control={control} loading={loading} orderTotal={orderTotal} />
          ) : null}

          {outcome === 'inspector_penalty' ? (
            <PenaltyFields
              control={control}
              loading={loading}
              penaltyTypes={ADMIN_APPEAL_PENALTY_TYPES}
              showDays={penaltyType === 'suspension'}
            />
          ) : null}

          <DecisionReasonField control={control} loading={loading} />
          <AdminNotesField control={control} loading={loading} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || !isValid || Boolean(errors.outcome)}
            loading={loading}
          >
            {t('appeals.resolveDialog.confirm')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
